import React, { useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Layout as LayoutIcon, Star } from "lucide-react";

// ============================================================
// Invoice Layout Templates Manager
// ============================================================
export function TemplatesManagerModal({ templates, onClose, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const remove = async (t) => {
    if (t.id === "default") { toast.error("Built-in default cannot be deleted"); return; }
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/invoice-templates/${t.id}`); toast.success("Template deleted"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const setAsDefault = async (t) => {
    if (t.id === "default") return;
    try {
      await api.patch(`/invoice-templates/${t.id}`, { is_default: true });
      toast.success(`"${t.name}" is now the default template`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return createPortal((
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-3xl w-full flex flex-col min-h-0" style={{ maxHeight: "calc(100vh - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 bg-white shrink-0">
          <div>
            <h2 className="font-heading text-xl font-black">Invoice Templates</h2>
            <p className="font-mono text-[11px] text-zinc-500">Tailor headers, totals tint, bank details, and footers per business unit.</p>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0 space-y-3">
          <div className="flex justify-end">
            <button onClick={() => setShowNew(true)} className="brut-btn px-3 py-1.5 text-xs flex items-center gap-1" data-testid="tmpl-new-btn"><Plus className="w-3.5 h-3.5" /> NEW TEMPLATE</button>
          </div>
          {templates.map((t) => (
            <div key={t.id} className="border-2 border-zinc-900 p-3 flex items-center justify-between gap-3" data-testid={`tmpl-row-${t.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 border-2 border-zinc-900 flex items-center justify-center" style={{ background: t.accent_hex || "#FEF08A" }}><LayoutIcon className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <div className="font-bold truncate flex items-center gap-2">
                    {t.name}
                    {t.is_default && <span className="font-mono text-[10px] uppercase bg-green-200 border border-zinc-900 px-1.5 py-0.5 flex items-center gap-1"><Star className="w-3 h-3" /> DEFAULT</span>}
                    {t.id === "default" && <span className="font-mono text-[10px] uppercase bg-zinc-100 border border-zinc-900 px-1.5 py-0.5">BUILT-IN</span>}
                  </div>
                  <div className="font-mono text-[11px] text-zinc-500 truncate">{t.company_name || "—"} · {t.page_orientation || "landscape"} · accent {t.accent_hex}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!t.is_default && t.id !== "default" && (
                  <button onClick={() => setAsDefault(t)} className="brut-btn-secondary px-2 py-1 text-[10px] font-mono" data-testid={`tmpl-default-${t.id}`}>SET DEFAULT</button>
                )}
                <button onClick={() => setEditing(t)} className="brut-btn-secondary px-2 py-1 text-[10px] font-mono" data-testid={`tmpl-edit-${t.id}`}>{t.id === "default" ? "VIEW" : "EDIT"}</button>
                {t.id !== "default" && (
                  <button onClick={() => remove(t)} className="text-red-600 p-1" data-testid={`tmpl-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <div className="text-center py-10 text-zinc-400 font-mono text-xs">NO TEMPLATES YET</div>
          )}
        </div>
      </div>
      {(showNew || editing) && (
        <TemplateEditorModal
          template={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); onChanged(); }}
        />
      )}
    </div>
  ), document.body);
}

function TemplateEditorModal({ template, onClose, onSaved }) {
  const readonly = template?.id === "default";
  const [form, setForm] = useState(() => ({
    name: "", header_title: "INVOICE", header_subtitle: "",
    company_name: "", company_address: "", company_gstin: "", company_pan: "",
    company_phone: "", company_email: "", company_website: "",
    bank_details: "", footer_text: "", terms_text: "",
    accent_hex: "#FEF08A", primary_hex: "#18181B",
    show_logo: true, page_orientation: "landscape", is_default: false,
    ...(template || {}),
  }));
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (readonly) { onClose(); return; }
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (template && template.id && template.id !== "default") {
        await api.patch(`/invoice-templates/${template.id}`, form);
      } else {
        await api.post("/invoice-templates", form);
      }
      toast.success(template ? "Template updated" : "Template created");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return createPortal((
    <div className="fixed inset-0 bg-zinc-900/60 z-[60] flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-3xl w-full flex flex-col min-h-0" style={{ maxHeight: "calc(100vh - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 bg-white shrink-0">
          <h2 className="font-heading text-xl font-black">{readonly ? "Built-in Default (Read-only)" : (template ? "Edit Template" : "New Template")}</h2>
          <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
        </div>
        <form id="tmpl-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 px-5 py-4 overflow-y-auto flex-1 min-h-0">
          <TF label="TEMPLATE NAME" required>
            <input required disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm" value={form.name ?? ""} onChange={(e) => upd("name", e.target.value)} data-testid="tmpl-name-input" />
          </TF>
          <TF label="HEADER TITLE">
            <input disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm" value={form.header_title ?? ""} onChange={(e) => upd("header_title", e.target.value)} />
          </TF>
          <TF label="HEADER SUBTITLE" full>
            <input disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm" placeholder="e.g. Tax Invoice / Proforma" value={form.header_subtitle ?? ""} onChange={(e) => upd("header_subtitle", e.target.value)} />
          </TF>
          <TF label="COMPANY NAME">
            <input disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm" value={form.company_name ?? ""} onChange={(e) => upd("company_name", e.target.value)} />
          </TF>
          <TF label="COMPANY GSTIN">
            <input disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm font-mono" value={form.company_gstin ?? ""} onChange={(e) => upd("company_gstin", e.target.value)} />
          </TF>
          <TF label="COMPANY ADDRESS" full>
            <textarea disabled={readonly} rows={2} className="brut-input w-full px-2.5 py-1.5 text-sm" value={form.company_address ?? ""} onChange={(e) => upd("company_address", e.target.value)} />
          </TF>
          <TF label="PHONE">
            <input disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm font-mono" value={form.company_phone ?? ""} onChange={(e) => upd("company_phone", e.target.value)} />
          </TF>
          <TF label="EMAIL">
            <input disabled={readonly} type="email" className="brut-input w-full px-2.5 py-1.5 text-sm font-mono" value={form.company_email ?? ""} onChange={(e) => upd("company_email", e.target.value)} />
          </TF>
          <TF label="WEBSITE">
            <input disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm font-mono" value={form.company_website ?? ""} onChange={(e) => upd("company_website", e.target.value)} />
          </TF>
          <TF label="PAN">
            <input disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm font-mono" value={form.company_pan ?? ""} onChange={(e) => upd("company_pan", e.target.value.toUpperCase())} />
          </TF>
          <TF label="BANK DETAILS (multiline)" full>
            <textarea disabled={readonly} rows={3} placeholder={"HDFC Bank\n0012345678901\nIFSC: HDFC0001234"} className="brut-input w-full px-2.5 py-1.5 text-sm font-mono" value={form.bank_details ?? ""} onChange={(e) => upd("bank_details", e.target.value)} />
          </TF>
          <TF label="TERMS TEXT" full>
            <textarea disabled={readonly} rows={2} className="brut-input w-full px-2.5 py-1.5 text-sm" value={form.terms_text ?? ""} onChange={(e) => upd("terms_text", e.target.value)} />
          </TF>
          <TF label="FOOTER TEXT" full>
            <input disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm" value={form.footer_text ?? ""} onChange={(e) => upd("footer_text", e.target.value)} />
          </TF>
          <div className="col-span-full border border-zinc-900 bg-amber-50 p-2 text-[10px] font-mono text-zinc-700" data-testid="template-placeholders-hint">
            <b>AUTO-ADAPT PER CLIENT:</b> use placeholders in Header Subtitle / Terms / Footer / Bank Details — they fill from each client's saved details at PDF time:
            {" {{client_name}} {{contact_person}} {{client_email}} {{client_phone}} {{client_address}} {{gst_number}} {{rate_per_job}} {{rate_per_hour}} {{currency}} {{invoice_number}} {{period_start}} {{period_end}} {{total}} {{job_count}}"}
          </div>
          <TF label="ACCENT COLOUR (totals row)">
            <div className="flex items-center gap-2">
              <input disabled={readonly} type="color" className="w-12 h-9 border-2 border-zinc-900" value={form.accent_hex ?? "#FEF08A"} onChange={(e) => upd("accent_hex", e.target.value)} />
              <input disabled={readonly} className="brut-input flex-1 px-2 py-1.5 text-sm font-mono" value={form.accent_hex ?? "#FEF08A"} onChange={(e) => upd("accent_hex", e.target.value)} />
            </div>
          </TF>
          <TF label="PRIMARY COLOUR (header)">
            <div className="flex items-center gap-2">
              <input disabled={readonly} type="color" className="w-12 h-9 border-2 border-zinc-900" value={form.primary_hex ?? "#18181B"} onChange={(e) => upd("primary_hex", e.target.value)} />
              <input disabled={readonly} className="brut-input flex-1 px-2 py-1.5 text-sm font-mono" value={form.primary_hex ?? "#18181B"} onChange={(e) => upd("primary_hex", e.target.value)} />
            </div>
          </TF>
          <TF label="ORIENTATION">
            <select disabled={readonly} className="brut-input w-full px-2.5 py-1.5 text-sm font-mono" value={form.page_orientation ?? "landscape"} onChange={(e) => upd("page_orientation", e.target.value)}>
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
            </select>
          </TF>
          <TF label="OPTIONS">
            <div className="space-y-1 mt-1.5">
              <label className="flex items-center gap-2 text-sm font-mono"><input type="checkbox" disabled={readonly} checked={!!form.show_logo} onChange={(e) => upd("show_logo", e.target.checked)} /> Show client logo</label>
              <label className="flex items-center gap-2 text-sm font-mono"><input type="checkbox" disabled={readonly} checked={!!form.is_default} onChange={(e) => upd("is_default", e.target.checked)} data-testid="tmpl-default-toggle" /> Set as default</label>
            </div>
          </TF>
        </form>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-900 bg-white shrink-0">
          <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">{readonly ? "CLOSE" : "CANCEL"}</button>
          {!readonly && (
            <button type="submit" form="tmpl-form" disabled={saving} className="brut-btn px-4 py-2 text-sm disabled:opacity-50" data-testid="tmpl-save-btn">{saving ? "SAVING..." : (template ? "SAVE" : "CREATE")}</button>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}

const TF = ({ label, children, full, required }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <div className="label-mono mb-1 text-xs">{label}{required && <span className="text-red-600"> *</span>}</div>
    {children}
  </label>
);
